import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [error, setError] = useState(null);
  const hasAttemptedFetch = useRef(false);
  const userRequestRef = useRef(null);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setError(null);
    hasAttemptedFetch.current = false;
    userRequestRef.current = null;
  }, []);

  // Listen for soft unauthorized events to avoid hard reloads
  useEffect(() => {
    const authHandler = () => {
      logout();
    };
    window.addEventListener('auth-unauthorized', authHandler);
    return () => window.removeEventListener('auth-unauthorized', authHandler);
  }, [logout]);

  // Auto-logout barista when subscription expires mid-session
  useEffect(() => {
    const subHandler = (e) => {
      // Only act if currently authenticated barista
      if (!user || user.role !== 'BARISTA') return;
      const { subscriptionStatus } = e.detail || {};
      if (!subscriptionStatus) return;
      if (['EXPIRED','NONE','TRIAL_EXPIRED'].includes(subscriptionStatus)) {
        try {
          sessionStorage.setItem('baristaSubscriptionExpired', '1');
        } catch (_) {/* ignore */}
        logout();
      }
    };
    window.addEventListener('subscription-required', subHandler);
    return () => window.removeEventListener('subscription-required', subHandler);
  }, [user, logout]);

  const fetchUser = useCallback(async () => {
    if (hasAttemptedFetch.current) {
      return;
    }
    if (userRequestRef.current) {
      return userRequestRef.current;
    }
    try {
      hasAttemptedFetch.current = true;
      setError(null);
      userRequestRef.current = api.get('/auth/me');
      const response = await userRequestRef.current;
      setUser(response.data);
    } catch (error) {
      setError(error);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
      userRequestRef.current = null;
    }
  }, []);

  const handleUserFetch = useCallback(() => {
    if (token && !hasAttemptedFetch.current) {
      fetchUser();
    } else if (!token) {
      setLoading(false);
      setError(null);
      hasAttemptedFetch.current = true;
    }
  }, [token, fetchUser]);

  useEffect(() => {
    handleUserFetch();
  }, [handleUserFetch]);

  const setAuthData = useCallback((newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    hasAttemptedFetch.current = true;
  }, []);

  const login = useCallback(async (email, password, recaptchaToken) => {
    try {
      setError(null);
      const payload = { email, password };
      if (recaptchaToken) {
        payload.recaptchaToken = recaptchaToken;
      }
      const response = await api.post('/auth/login', payload);
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      hasAttemptedFetch.current = true;
      return { success: true, user: userData };
    } catch (error) {
      setError(error);
      const status = error.response?.status;
      const errorType = error.response?.data?.errorType;
      if (status === 402 && errorType === 'SUBSCRIPTION_REQUIRED') {
        return {
          success: false,
            error: error.response?.data?.error || 'Subscription inactive',
          errorKey: 'barista.subscriptionInactive'
        };
      }
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed'
      };
    }
  }, []);

  const register = useCallback(async (userData, recaptchaToken) => {
    try {
      setError(null);
      const payload = { ...userData };
      if (recaptchaToken) {
        payload.recaptchaToken = recaptchaToken;
      }
      const response = await api.post('/auth/register', payload);
      const { token: newToken, user: userInfo } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userInfo);
      hasAttemptedFetch.current = true;
      return { success: true };
    } catch (error) {
      setError(error);
      return {
        success: false,
        error: error.response?.data?.message || 'Registration failed'
      };
    }
  }, []);

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    setAuthData,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};